// Discount pricing helpers based on the currently logged-in customer.
//
// Priority: per-category discount > global discount > none.
// A product's category discount only applies if the product's own category_id
// matches a key in customer.discount_by_category (we do NOT climb the tree —
// the admin sets the discount on the exact category they want).

import { useCustomerAuth } from '../context/CustomerAuthContext';

export function computeDiscountPercent(customer, categoryId) {
  if (!customer) return 0;
  const byCat = customer.discount_by_category || {};
  if (categoryId && byCat[categoryId] > 0) return Number(byCat[categoryId]);
  if (customer.discount_all > 0) return Number(customer.discount_all);
  return 0;
}

export function applyDiscount(price, percent) {
  if (!price || !percent) return price || 0;
  return Math.max(0, price * (1 - percent / 100));
}

// Weekend price: if manually set (>0), use it. Otherwise price_per_day * 1.5.
export function computeWeekendPrice(product) {
  const day = Number(product?.price_per_day || 0);
  const wk = Number(product?.price_per_weekend || 0);
  if (wk > 0) return wk;
  return day > 0 ? day * 1.5 : 0;
}

// Hook used inside components — returns a function `priceOf(product)` that
// yields daily and weekend pricing (both original and discounted) for the
// current user.
export function usePricing() {
  const { customer } = useCustomerAuth();
  return (productOrItem) => {
    const original = Number(productOrItem?.price_per_day || 0);
    const categoryId = productOrItem?.category_id || productOrItem?.product_category_id || null;
    const percent = computeDiscountPercent(customer, categoryId);
    const discounted = applyDiscount(original, percent);
    const weekendOriginal = computeWeekendPrice(productOrItem);
    const weekendDiscounted = applyDiscount(weekendOriginal, percent);
    return {
      original,
      discounted,
      percent,
      hasDiscount: percent > 0 && original > 0,
      weekendOriginal,
      weekendDiscounted,
      weekendIsAuto: !(Number(productOrItem?.price_per_weekend || 0) > 0),
    };
  };
}

